import { stashCardImageUrl, type StashThumbnailData } from "@/components/shared/StashThumbnail/StashThumbnail";
import { STASHT_STATIC_PIN } from "@/components/venues/stashtMarkerSvg";
import { CONFIG } from "@/config";
import { useAuth } from "@/contexts/AuthContext";
import { useNavigationStackSafe } from "@/contexts/NavigationStackContext";
import { useTheme } from "@/contexts/PreferencesContext";
import { useIsTabActive } from "@/contexts/TabActivityContext";
import { STARTER_PROMPTS, useChatThreads } from "@/features/chat/chatThreads";
import { useCollectionInventory } from "@/hooks/useCollections";
import { useProfile } from "@/hooks/useProfile";
import { useRecentStashes } from "@/hooks/useRecentStashes";
import { useRoundupActivity, useRoundupsList } from "@/hooks/useRoundups";
import { useStashPosts } from "@/hooks/useStashPosts";
import { useTagsWithCounts } from "@/hooks/useTagsWithCounts";
import { useUpcomingEvents } from "@/hooks/useUpcomingEvents";
import { staticMapImageUrl } from "@/lib/map/staticMapImage";
import { getEventRelativeDate, isPastEvent } from "@/utils/eventDateUtils";
import type { PlaceablePage } from "./homeLayout";

export type Cover =
  | { images: string[]; caption?: { title: string; detail?: string }; credit?: string }
  | { tags: { name: string; color: string }[] }
  | { bubbles: string[] }
  | null;

export interface Preview {
  cover: Cover;
  line?: string;
}

const NO_IDS: string[] = [];
/** The card's 9:8 cover. One size at every breakpoint, so its image stays cached. */
const MAP_COVER_SIZE = { width: 320, height: 284 };

const counted = (n: number | undefined, one: string, many: string) =>
  n === undefined ? undefined : `${n.toLocaleString()} ${n === 1 ? one : many}`;

const imageCover = (urls: (string | undefined)[]): Cover => {
  const images = urls.filter((url): url is string => Boolean(url)).slice(0, 4);
  return images.length > 0 ? { images } : null;
};

/** The last one or two chats as bubbles; before any, what Chat's welcome offers. */
const useChatPreview = (shown: boolean): Preview => {
  const { user } = useAuth();
  const active = useIsTabActive();
  const threads = useChatThreads(user?.user_id, shown && active);
  const titles = [...threads]
    .sort((a, b) => b.updatedAt.localeCompare(a.updatedAt))
    .slice(0, 2)
    .map(thread => thread.title);
  return titles.length > 0
    ? { cover: { bubbles: titles }, line: counted(threads.length, "chat", "chats") }
    : { cover: { bubbles: STARTER_PROMPTS.slice(0, 2) }, line: "Get started here" };
};

/** The last map view, or the account's home area, with a pin at its center. */
const useMapCover = (): Cover => {
  const { user } = useAuth();
  const { theme } = useTheme();
  const view = useNavigationStackSafe()?.getMapView();
  const homeLat = parseFloat(String(user?.home_coordinates?.lat));
  const homeLng = parseFloat(String(user?.home_coordinates?.lng));
  const center = view
    ? { lat: view.latitude, lng: view.longitude }
    : Number.isFinite(homeLat) && Number.isFinite(homeLng) ? { lat: homeLat, lng: homeLng } : null;
  const token = CONFIG.MAPBOX_ACCESS_TOKEN;
  if (!center || !token) return null;
  const url = staticMapImageUrl({
    ...MAP_COVER_SIZE,
    token,
    theme: theme === "dark" ? "dark" : "light",
    places: [center],
    zoom: view?.zoom,
    placePin: STASHT_STATIC_PIN,
    devicePixelRatio: window.devicePixelRatio,
  });
  return url ? { images: [url], credit: "©\u00A0Mapbox ©\u00A0OpenStreetMap" } : null;
};

/** The next event, preferring one with a picture, with the Events page's own wording. */
const useEventsPreview = (shown: boolean): Preview => {
  const { data } = useUpcomingEvents(30, 6, shown);
  const events = (data?.events ?? []).filter(event => !isPastEvent(event));
  const next = events.find(event => event.image_url) ?? events[0];
  return {
    cover: next
      ? {
          images: next.image_url ? [next.image_url] : [],
          caption: { title: next.name, detail: getEventRelativeDate(next, false) ?? undefined },
        }
      : null,
    line: data ? `${data.total.toLocaleString()} upcoming` : undefined,
  };
};

/** The latest edition's cover and subject, and the new saves its page leads with. */
const useRoundupsPreview = (shown: boolean): Preview => {
  const { data } = useRoundupsList({ enabled: shown });
  const { data: activity } = useRoundupActivity({ enabled: shown });
  const latest = data?.pages[0]?.roundups[0];
  const coverId = latest?.cover_stash_id;
  const { posts } = useStashPosts(shown && coverId ? [coverId] : NO_IDS);
  const image = posts[0] && stashCardImageUrl(posts[0] as unknown as StashThumbnailData);
  return {
    cover: latest ? { images: image ? [image] : [], caption: { title: latest.subject } } : null,
    line: activity?.stash_count === 0 ? "No new saves yet!" : counted(activity?.stash_count, "new save", "new saves"),
  };
};

/**
 * Each page's cover and count line, read from what the app already holds (native
 * `StashesSections`): collection covers and item counts, the newest stash images,
 * the four most recent tags, the profile's account totals, and the device's chats
 * and last map view. Events and Roundups add a small cached read, made only while
 * they're `cards` (a phone's shortcut tiles have no cover). Pages without one yet
 * show the native empty state, their symbol on an empty cover. Explore is one of
 * those: its feed takes seconds, so Home never runs it.
 */
export function useHomePreviews(cards: PlaceablePage[]): Partial<Record<PlaceablePage, Preview>> {
  const { data: profile } = useProfile();
  const { data: inventory } = useCollectionInventory();
  const { data: recent } = useRecentStashes();
  const { data: tags } = useTagsWithCounts();
  const chat = useChatPreview(cards.includes("chat"));
  const mapCover = useMapCover();
  const calendar = useEventsPreview(cards.includes("calendar"));
  const roundups = useRoundupsPreview(cards.includes("roundups"));
  const stats = profile?.stats;
  return {
    faves: {
      cover: imageCover(inventory?.favourites?.covers ?? []),
      line: counted(inventory?.favourites?.items, "item", "items"),
    },
    done: { cover: imageCover(inventory?.done?.covers ?? []), line: counted(inventory?.done?.items, "item", "items") },
    "all-stashes": {
      cover: imageCover(
        (recent ?? []).slice(0, 8).map(post => stashCardImageUrl(post as unknown as StashThumbnailData)),
      ),
      line: counted(stats?.total_stashes, "stash", "stashes"),
    },
    tags: {
      cover: tags.length > 0 ? { tags: tags.slice(0, 4).map(tag => ({ name: tag.name, color: tag.color })) } : null,
      line: counted(stats?.total_tags, "tag", "tags"),
    },
    collections: {
      cover: imageCover(inventory?.covers ?? []),
      line: counted(inventory?.count, "collection", "collections"),
    },
    map: {
      cover: mapCover,
      line: stats?.total_places === 0 ? "Stashed near you!" : counted(stats?.total_places, "place", "places"),
    },
    chat,
    calendar,
    roundups,
    discover: { cover: null, line: "Find something new" },
  };
}
